import { useCallback, useRef } from "react";
import notify from "devextreme/ui/notify";
import { convertToFlatJSON } from "../../../utils/objectUtils.js";
import { fetchAgentsOnce } from "../../../utils/agentApiCache.js";
import { getParameterFromUrl } from "../utils/urlUtils.js";
import { fetchTaskData, fetchTemplateFromApi } from "../services/formApiService.js";
import { getEncryptionKey } from "../services/formFileService.js";
import DataSetManager from "../../../utils/dataSetManager.js";

const { REACT_APP_SERVER_URL } = process.env;

export default function useFormLoader({ docRef, templateRef, settingsRef, updateState }) {
  const encryptionKey = getEncryptionKey();

  const countVisibleTabs = useCallback((tabs, evaluateVisibility) => {
    if (!tabs) return 0;
    return tabs.filter((item) => evaluateVisibility(item.visible) === true).length;
  }, []);

  const fetchTemplate = useCallback(async (data, fid, evaluateVisibility) => {
    try {
      const grouped = await fetchTemplateFromApi(data.UrlToApi, fid);
      templateRef.current = grouped;

      // Load DataSets from all fields
      const allFields = [];
      Object.values(grouped).forEach(fields => {
        if (Array.isArray(fields)) {
          allFields.push(...fields);
        }
      });
      await DataSetManager.loadAllDataSets(allFields);

      updateState({
        isFirsTimeLoaded: true,
        XLClient: grouped.XLClient?.[0]?.Value,
        XLAccount: grouped.XLAccount?.[0]?.Value,
        numOfVisibleTabs: countVisibleTabs(grouped.tabs, evaluateVisibility),
        dataSetsLoaded: true,
      });

      // 3-second delay before disabling defaults (preserves original timing)
      setTimeout(() => {
        updateState({ setDefaults: false });
      }, 3000);

      return grouped;
    } catch (error) {
      console.error("Failed to fetch or process template:", error);
      notify({ message: "אירעה שגיאה בעת טעינת הנתונים." }, "error", 4000);
    }
  }, [templateRef, updateState, countVisibleTabs]);

  const findRoleSelectBoxField = useCallback(() => {
    const template = templateRef.current;
    if (!template) return null;
    for (const key in template) {
      if (Array.isArray(template[key])) {
        const field = template[key].find((item) => item.Type === "roleSelectBox");
        if (field) return field;
      }
    }
    return null;
  }, [templateRef]);

  const loadAgentList = useCallback(() => {
    const roleField = findRoleSelectBoxField();
    const role = roleField?.Value || "License";

    fetchAgentsOnce(role, REACT_APP_SERVER_URL)
      .then((data) => {
        if (data && Array.isArray(data)) {
          updateState({ agentArrWithId: data });
        } else {
          updateState({ agentArrWithId: [] });
        }
      })
      .catch(() => {
        updateState({ agentArrWithId: [] });
      });
  }, [findRoleSelectBoxField, updateState]);

  const GetDataFromDB = useCallback((fid, userData, evaluateVisibility) => {
    const urlParameterU = getParameterFromUrl("u");
    let urlParameterF;

    if (urlParameterU === undefined) {
      urlParameterF = getParameterFromUrl("f");
    }

    const data = userData.data.form.listOfForms.find((f) => {
      if (urlParameterU !== undefined) {
        return f.UrlToApi.substring(35) === urlParameterU;
      } else if (urlParameterF != undefined) {
        return f.ID == urlParameterF;
      }
      return false;
    });

    settingsRef.current.agentId = userData.data.user.agentId;
    settingsRef.current.roles = userData.data.user.roles;
    settingsRef.current.isLicense = settingsRef.current.roles.includes("License");

    updateState({ dataFromDB: data, user: userData.data.user });
    fetchTemplate(data, fid, evaluateVisibility);
  }, [settingsRef, updateState, fetchTemplate]);

  const loadValuesByTaskId = useCallback(async () => {
    updateState({ isLoading: true });
    const taskId = getParameterFromUrl("t");

    if (taskId) {
      updateState({ setDefaults: false });
      try {
        const res = await fetchTaskData(taskId);
        updateState({ sourceStatus: res.sourceStatus, draftName: res.name });

        const decryptedData = encryptionKey.decrypt(res.txtData)
          ? encryptionKey.decrypt(res.txtData)
          : res.txtData;
        let filteredData = JSON.parse(decryptedData);

        Object.keys(filteredData).forEach((key) => {
          if (key.includes("fileUploader")) {
            delete filteredData[key];
          }
        });

        docRef.current = filteredData;

        const names = filteredData.owner.map(
          (item) => `${item.firstName} ${item.lastName}  `
        );
        updateState({ ownersNames: names, isLoading: false });
      } catch (error) {
        notify("שגיאה בטעינת הנתונים", "error", 10000);
        updateState({ isLoading: false });
      }
    } else {
      updateState({ isLoading: false });
    }
  }, [docRef, updateState, encryptionKey]);

  const loadValuesFromUrl = useCallback(() => {
    const data = getParameterFromUrl("d");
    if (data == null) return;

    const json = convertToFlatJSON(data);

    Object.entries(json).forEach(([key, value]) => {
      const keyParts = key.split(".");
      const objectKey = keyParts[0];
      const fieldName = keyParts[1];

      const objectMatch = objectKey.match(/^(\w+)(?:\[(\d+)\])?$/);
      if (!objectMatch) return;

      const objectName = objectMatch[1];
      const indexNum = objectMatch[2] ? parseInt(objectMatch[2], 10) : null;

      let parsedValue = value;
      if (value.toLowerCase() === "true") {
        parsedValue = true;
      } else if (value.toLowerCase() === "false") {
        parsedValue = false;
      } else if (!isNaN(value) && value.trim() !== "") {
        parsedValue = parseFloat(value);
      }

      if (indexNum !== null) {
        if (!docRef.current[objectName]) docRef.current[objectName] = [];
        if (!docRef.current[objectName][indexNum]) docRef.current[objectName][indexNum] = {};
        docRef.current[objectName][indexNum][fieldName] = parsedValue;
      } else {
        if (!docRef.current[objectName]) docRef.current[objectName] = {};
        docRef.current[objectName][fieldName] = parsedValue;
      }
    });
  }, [docRef]);

  const initializeComponent = useCallback((userData, evaluateVisibility) => {
    const fId = getParameterFromUrl("f");
    updateState({ formId: fId });
    GetDataFromDB(fId, userData, evaluateVisibility);
    loadValuesFromUrl();
    loadAgentList();
  }, [updateState, GetDataFromDB, loadValuesFromUrl, loadAgentList]);

  return {
    initializeComponent,
    loadValuesByTaskId,
    fetchTemplate,
    GetDataFromDB,
    loadValuesFromUrl,
    loadAgentList,
    countVisibleTabs,
  };
}
